import {createAsyncThunk} from '@reduxjs/toolkit';
import {MapNode} from 'common/src/modules/nodes/nodes.types';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {PatchTreeParams} from 'common/src/modules/campaignVersions/campaignVersions.api';
import getCampaignNodes from 'common/src/modules/nodes/utils/getCampaignNodes';
import {RootState} from 'common/src/redux/store';
import {updateCampaignNodes} from './updateCampaignNodes';
import {setNextNodeId} from '../campaignLogic.slice';
import campaignsApi from '../../../campaigns.api';

/**
 * Batch updates cards in the campaign (add and/or update multiple cards at once).
 * @param props - The properties containing nodes to add and update.
 * @param dispatch - The dispatch function.
 * @param getState - The getState function.
 * @returns The response from the API.
 * @description This thunk performs batch operations on campaign cards.
 * It gets the campaign and version number from the state,
 * calls the API to add/update multiple nodes in a single operation,
 * and updates the nodesRef and redux state with the new nodes.
 */
export const batchUpdateCards = createAsyncThunk(
  `${BreadcrumbName.CampaignLogic}/batchUpdateCards`,
  async (
    props: {
      nodesToAdd: MapNode[];
      nodesToUpdate: MapNode[];
      campaignTreeType: CampaignTreeType;
    },
    {dispatch, getState},
  ) => {
    const {nodesToAdd, nodesToUpdate, campaignTreeType} = props;

    // Get campaign data from Redux state
    const state = getState() as RootState;
    const campaign = state.campaignPage?.campaignTree?.campaign;
    const versionNumber = state.campaignPage?.campaignTree?.versionNumber;

    if (!campaign || !versionNumber) {
      throw new Error('Campaign or version number not found in state');
    }

    // Call API to batch update nodes
    const basePayload: PatchTreeParams = {
      campaignId: campaign.id,
      versionNumber,
      nodesToAdd,
      nodesToUpdate,
      nodesToDelete: [],
      campaignNode: campaign,
    };

    const response = await campaignsApi.updateTree(basePayload);

    // Get updated nodes from response and update nodesRef
    const campaignNodes = getCampaignNodes(response);
    dispatch(updateCampaignNodes({connectedNodes: campaignNodes, campaignTreeType}));
    dispatch(setNextNodeId(response.nextNodeId));
  },
);
